import type { CityConfig } from "../city-template/types";

export const rockvilleConfig: CityConfig = {
  city: "Rockville",
  state: "Maryland",
  stateAbbr: "MD",
  slug: "rockville",
  route: "/cities/rockville",
  title: "Emergency Passport Rockville MD | Expedited Passport Processing | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Rockville, Maryland — as fast as 24 hours. Fast passport renewal and new passport applications for Rockville, Gaithersburg, Potomac, and North Potomac residents. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Rockville, MD",
  heroSubtitle: "Rockville travelers — from the county seat's government center to the tech corridor to diverse international neighborhoods — trust Fast Passport Center for emergency and expedited passports as fast as 24 hours. A+ BBB rated courier service serving all of Montgomery County.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Beautiful%20view%20of%20Rockville%20Maryland%20Town%20Center%20with%20modern%20courthouse%20building%2C%20historic%20downtown%20area%2C%20tree-lined%20streets%2C%20red%20brick%20sidewalks%2C%20contemporary%20mixed-use%20buildings%2C%20warm%20golden%20afternoon%20sunlight%2C%20professional%20suburban%20city%20photography%2C%20no%20visible%20text%20or%20people%2C%20clean%20architectural%20details%20and%20green%20landscaping&width=1800&height=700&seq=rockville-hero-001&orientation=landscape",
  heroImageAlt: "View of Rockville Maryland Town Center with modern courthouse and historic downtown area",
  geoPosition: "39.0837;-77.1528",
  geoPlacename: "Rockville, Maryland",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Washington DC Passport Agency",
    address: "600 19th St NW, 1st Floor, Washington, DC 20006",
    distance: "Approximately 35–45 minutes from Rockville via I-270 S",
  },
  nearbyCitiesZips: [
    { name: "Rockville", zips: "20847, 20848, 20849, 20850, 20851, 20852, 20853, 20857" },
    { name: "Gaithersburg", zips: "20877, 20878, 20879, 20882, 20883, 20884, 20885, 20898, 20899" },
    { name: "Potomac", zips: "20854, 20859" },
    { name: "Derwood", zips: "20855" },
    { name: "North Potomac", zips: "20878" },
    { name: "Aspen Hill", zips: "20853, 20851" },
    { name: "Olney", zips: "20830, 20832, 20833" },
    { name: "Germantown", zips: "20874, 20875, 20876" },
    { name: "Clarksburg", zips: "20871, 20872" },
    { name: "Boyds", zips: "20841" },
    { name: "Bethesda", zips: "20814, 20815, 20816, 20817, 20824, 20827, 20889, 20892, 20894" },
    { name: "Chevy Chase", zips: "20814, 20815, 20825" },
    { name: "Silver Spring", zips: "20901, 20902, 20903, 20904, 20905, 20906, 20910, 20911" },
    { name: "Takoma Park", zips: "20901, 20910, 20912, 20913" },
    { name: "Kensington", zips: "20891, 20895" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier for Rockville and Montgomery County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-government-line", title: "County Seat Advantage", desc: "Serving Rockville government center and diverse international community" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no trip to downtown DC" },
  ],
  internalLinks: [
    { name: "Expedited passport in Bethesda, MD", route: "/cities/bethesda" },
    { name: "Emergency passport help in Gaithersburg, MD", route: "/cities/gaithersburg" },
    { name: "Urgent passport services in Silver Spring, MD", route: "/cities/silver-spring" },
    { name: "Maryland passport services overview", route: "/states/maryland" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Rockville, Maryland remotely via secure registered courier. We do not have a walk-in office in Rockville — our registered couriers hand-deliver your documents to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Rockville metro travelers without the drive to downtown DC.",
  fpcOfficeDistance: "Serving Rockville, Gaithersburg, Potomac, Derwood, and greater Montgomery County",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Rockville and Montgomery County. From the Rockville Town Center and Montgomery County government buildings to the diverse neighborhoods of Aspen Hill and Derwood, from the tech corridor near the I-270 to the suburban communities of Olney and Germantown — we serve all of Montgomery County's diverse communities.",
  nearbyCitiesIntro: "We serve Rockville and nearby communities including Gaithersburg, Potomac, Derwood, North Potomac, Aspen Hill, Olney, Germantown, Clarksburg, and Boyds. We also serve Bethesda, Chevy Chase, Silver Spring, Takoma Park, and Kensington residents who prefer our expedited service.",
  howItWorksIntro: "Most Rockville travelers complete everything online and ship documents overnight. No need to drive 35–45 minutes to downtown DC or navigate the I-270 corridor during rush hour.",
  mistakesIntro: "Rockville is one of the most diverse cities in the country with a large international population. Here are the most common passport mistakes we see from Rockville travelers.",
  testimonialsIntro: "Rockville travelers — from government employees to tech professionals to international families — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Rockville, MD? Here are the most common ones we hear from Montgomery County travelers.",
  statePageRoute: "/states/maryland",
  statePageName: "Maryland",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 35–45 min to downtown DC", "Full day commitment — challenging for working professionals"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Perfect for business and government travel deadlines", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Rockville travelers with immediate departure needs — as fast as 24 hours. Perfect for government missions, business trips, and international family emergencies.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Government and business travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Rockville adults with a passport issued within the last 15 years.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to Rockville address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Rockville residents and families — including international students and new immigrants.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Rockville residents who lost their passport before travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Rockville families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Rockville residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Rockville MD", description: "Urgent passport processing for Rockville Maryland travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Rockville MD", description: "Fast passport renewal for Rockville Maryland residents with a previously-issued passport." },
    { name: "First-Time Passport Rockville MD", description: "New passport application services for Rockville Maryland residents applying for their first passport." },
    { name: "Child Passport Rockville MD", description: "Minor passport services for Rockville Maryland families with children under 16." },
  ],
  testimonials: [
    {
      name: "Dr. Aisha M.",
      location: "Rockville, MD",
      rating: 5,
      text: "Conference in Dubai in 5 days. Fast Passport Center reviewed every document, caught a form issue, and delivered my passport in 36 hours. Essential for professionals with tight international travel schedules.",
      service: "Emergency Passport",
    },
    {
      name: "Carlos R.",
      location: "Gaithersburg, MD",
      rating: 5,
      text: "Family trip to Colombia with 10 days notice. Four passports processed in under a week. The agent handled everything in Spanish and English — we just shipped the documents from the UPS Store on Rockville Pike.",
      service: "Family Passport Bundle",
    },
    {
      name: "Patricia W.",
      location: "Potomac, MD",
      rating: 5,
      text: "Government assignment to Brussels with 7 days notice. Fast Passport Center had my renewed passport ready in 2 days. No need to drive to DC — everything shipped overnight.",
      service: "Passport Renewal",
    },
    {
      name: "James L.",
      location: "Derwood, MD",
      rating: 5,
      text: "Lost my passport before a business trip to Seoul. They walked me through the replacement process and had it in my hands in 48 hours. Saved my company's international deal.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "rockville-1",
      name: "Rockville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2 N Washington St",
      city: "Rockville",
      state: "MD",
      zip: "20850",
      phone: "(301) 762-8067",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Rockville, near the Rockville Town Center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Rockville acceptance facility. Appointments required — book 1–2 weeks in advance. Convenient Metro access via Rockville Station on the Red Line.",
      icon: "ri-mail-line",
    },
    {
      id: "rockville-2",
      name: "Gaithersburg Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1 N Summit Ave",
      city: "Gaithersburg",
      state: "MD",
      zip: "20877",
      phone: "(301) 948-3010",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Gaithersburg, approximately 5 miles northwest of Rockville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Gaithersburg and northern Montgomery County residents. Call to confirm current passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "rockville-3",
      name: "Potomac Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10601 River Rd",
      city: "Potomac",
      state: "MD",
      zip: "20854",
      phone: "(301) 983-2182",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Potomac, approximately 7 miles southwest of Rockville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Potomac and western Montgomery County residents. Ample parking available.",
      icon: "ri-mail-line",
    },
    {
      id: "rockville-4",
      name: "Derwood Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "16200 Redland Rd",
      city: "Derwood",
      state: "MD",
      zip: "20855",
      phone: "(301) 926-1114",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Derwood, approximately 4 miles east of Rockville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serving Derwood and eastern Montgomery County residents. Good alternative when Rockville appointments are full.",
      icon: "ri-mail-line",
    },
    {
      id: "rockville-5",
      name: "Montgomery County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "50 Maryland Ave",
      city: "Rockville",
      state: "MD",
      zip: "20850",
      phone: "(240) 777-9460",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Downtown Rockville, adjacent to the Rockville Town Center",
      website: "https://www.montgomerycountymd.gov/",
      websiteLabel: "Montgomery County",
      notes: "County-level facility with full passport acceptance. Extended hours compared to post office locations. Accessible via Metro Red Line to Rockville Station.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited DC slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 35–45 min drive to downtown DC", fpc: "No — ship from Rockville" },
    { feature: "Government Travel Support", agency: "No flexibility for official needs", fpc: "Yes — we handle government and corporate cases" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Rockville's busy government and tech professionals often discover passport needs just weeks before departure. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Local photo services in Rockville sometimes produce photos that meet general ID requirements but not passport standards. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at Rockville and Montgomery County acceptance facilities.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Not Booking the Acceptance Facility Early",
      description: "Rockville Post Office appointments fill up fast, especially during summer and holiday travel seasons. Book 2–3 weeks ahead.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Government fees change periodically. Always verify current fees before submitting — incorrect payment means starting over.",
    },
    {
      icon: "ri-time-line",
      title: "Underestimating Routine Processing Time",
      description: "Rockville residents often assume 4–6 weeks is enough. Routine processing takes 6–13 weeks. If your travel is within 10 weeks, use expedited service.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from your home in Rockville, Gaithersburg, or Potomac." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx location near Rockville Town Center or along Rockville Pike." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Rockville address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Rockville, MD?", a: "Fast Passport Center offers emergency passport processing in Rockville, Maryland in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Washington DC Passport Agency (35–45 minutes from Rockville) — without you needing to make the drive or fight I-270 traffic." },
    { q: "Does Rockville have its own passport agency?", a: "No — neither Rockville nor the state of Maryland has a regional passport agency. The closest facility is the Washington DC Passport Agency, approximately 35–45 minutes from Rockville via I-270 S. Fast Passport Center's courier service means Rockville residents never need to make that drive." },
    { q: "Can I get expedited passport services if I live in Gaithersburg or Potomac?", a: "Absolutely. Fast Passport Center serves all of the Rockville area including Gaithersburg, Potomac, Derwood, North Potomac, Aspen Hill, Olney, Germantown, and Clarksburg. We also serve Bethesda, Chevy Chase, Silver Spring, and Takoma Park residents. Our service works the same way — complete your application online, ship your documents, and we handle the rest." },
    { q: "How much does an expedited passport cost in Rockville, MD?", a: "Government fees are fixed: $130 for a first-time passport book or $130 + $60 expedite for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your departure timeline." },
    { q: "What airports do Rockville travelers use for international flights?", a: "Most Rockville residents fly internationally through Dulles International Airport (IAD), approximately 30–40 minutes west via I-270. Reagan National (DCA) is 30–40 minutes south via I-495. Baltimore-Washington International (BWI) is 45–55 minutes northeast. Always verify your passport is valid for at least 6 months past your return date." },
    { q: "Can I get a passport for my whole family in Rockville?", a: "Yes. Fast Passport Center handles multi-passport cases for families in Rockville, Gaithersburg, Potomac, and surrounding areas. Whether you need child passports, renewals, or first-time applications for the whole family, we coordinate the process to ensure everyone is ready to travel." },
    { q: "Where is the closest passport acceptance facility to Rockville?", a: "The Rockville Post Office at 2 N Washington St is the primary downtown acceptance facility. The Gaithersburg Post Office at 1 N Summit Ave serves northern Montgomery County. The Potomac Post Office at 10601 River Rd serves western Montgomery County. The Montgomery County Clerk's Office at 50 Maryland Ave also provides passport acceptance. All require appointments — book 1–2 weeks ahead." },
    { q: "How do I know if I need expedited or routine passport processing?", a: "If your travel is within 10 weeks, use expedited service. If your travel is within 2–3 weeks, use emergency expedited service. Routine processing takes 6–13 weeks and is only suitable for travel more than 3 months away. Rockville residents should plan ahead, especially for government assignments and international conferences." },
  ],
  photoLocations: [
    { name: "Walgreens — Rockville", address: "5815 Fishers Ln, Rockville, MD 20852", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Rockville", address: "1160 Rockville Pike, Rockville, MD 20852", hours: "Mon–Sun: 8AM–10PM" },
    { name: "UPS Store — Rockville", address: "199 E Montgomery Ave, Rockville, MD 20850", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "FedEx Office — Gaithersburg", address: "122 Kentlands Blvd, Gaithersburg, MD 20878", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    { name: "MVA — Gaithersburg", address: "15 Metropolitan Grove Rd, Gaithersburg, MD 20878", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Serves Montgomery County and northwest DC metro. REAL ID processing available.", featured: true },
    { name: "MVA — White Oak", address: "12335 Columbia Pike, Silver Spring, MD 20904", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Serves eastern Montgomery County and Silver Spring area. Full service including REAL ID.", featured: false },
  ],
  airports: [
    {
      code: "IAD",
      name: "Dulles International",
      distance: "30–40 minutes from Rockville via I-270 W",
      address: "1 Saarinen Cir, Dulles, VA 20166",
      note: "Primary international departure airport for Rockville travelers. Direct flights to London, Frankfurt, Tokyo, and major European hubs. United Airlines hub.",
      flightUrl: "https://www.flydulles.com/",
      flightLabel: "IAD Flight Info",
      featured: true,
    },
    {
      code: "DCA",
      name: "Reagan National (Washington DC)",
      distance: "30–40 minutes from Rockville via I-495 S",
      address: "2401 Smith Blvd, Arlington, VA 22202",
      note: "Secondary option for domestic connections to international hubs. Major business travel airport.",
      flightUrl: "https://www.flyreagan.com/",
      flightLabel: "DCA Flight Info",
      featured: false,
    },
    {
      code: "BWI",
      name: "Baltimore-Washington International Thurgood Marshall",
      distance: "45–55 minutes from Rockville via I-495 E",
      address: "7050 Friendship Rd, Baltimore, MD 21240",
      note: "Alternative for Southwest Airlines domestic connections and some Caribbean destinations.",
      flightUrl: "https://www.bwiairport.com/",
      flightLabel: "BWI Flight Info",
      featured: false,
    },
  ],
};